import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";

// WCAG 2.1 accessibility checks in development
if (import.meta.env.DEV) {
  import("@axe-core/react").then(({ default: axe }) => {
    axe(React, ReactDOM, 1000, {
      rules: [
        { id: "color-contrast", enabled: true },
        { id: "label", enabled: true },
        { id: "image-alt", enabled: true },
        // Exclude MUI tooltip/popper portals from landmark check (they render outside DOM tree)
        {
          id: "region",
          selector:
            '*:not([role="tooltip"]):not(.MuiPopper-root):not(.MuiModal-root)',
        },
      ],
    });
  });
}
import { I18nextProvider } from "react-i18next";
import i18next from "i18next";
import common_en from "./translations/en/common.json";
import common_de from "./translations/de/common.json";
import common_fr from "./translations/fr/common.json";
import common_nl from "./translations/nl/common.json";
import { store } from "./store";

import Auth0ProviderWithHistory from "./auth/auth0-provider-with-history";

import { Box, CircularProgress } from "@mui/material";
import { CONFIG } from "./config";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// Create a QueryClient instance with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes - increased for better caching
      gcTime: 30 * 60 * 1000, // 30 minutes - keep data in cache longer
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true, // Refetch when internet reconnects
    },
    mutations: {
      retry: 1,
    },
  },
});

const locale = "en";

// Get saved language preference or fall back to config default
const savedLanguage =
  localStorage.getItem("language") || CONFIG.defaultLanguage;

i18next.init({
  interpolation: { escapeValue: false }, // React already does escaping
  lng: savedLanguage, // language to use
  resources: {
    en: {
      common: common_en, // 'common' is our custom namespace
    },
    de: {
      common: common_de, // 'common' is our custom namespace
    },
    fr: {
      common: common_fr, // 'common' is our custom namespace
    },
    nl: {
      common: common_nl, // 'common' is our custom namespace
    },
  },
});

// loading component for suspense fallback
const Loader = () => (
  <Box sx={{ display: "flex" }}>
    <CircularProgress />
  </Box>
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Auth0ProviderWithHistory>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider local={locale} messages={common_en} i18n={i18next}>
          <Suspense fallback={<Loader />}>
            <Provider store={store}>
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <App />
                {import.meta.env.DEV && (
                  <ReactQueryDevtools initialIsOpen={false} />
                )}
              </BrowserRouter>
            </Provider>
          </Suspense>
        </I18nextProvider>
      </QueryClientProvider>
    </Auth0ProviderWithHistory>
  </React.StrictMode>
);
